// Every date the UI renders goes through this file, so "how a date
// looks" is decided in exactly one place. Before this, the same
// toLocaleString() call was copy-pasted into three components and three
// more one-off formats lived inside a fourth -- changing the house style
// meant finding all of them, and any one that got missed would silently
// disagree with the rest.
//
// Locale is left to the browser (`undefined`) rather than pinned to
// en-US, matching what these calls already did: what's fixed here is the
// *structure* of each format, not the language it renders in.

const asDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// The house format for a full timestamp:
//   "August 13, 2026, Thursday 6:08 AM"
//
// Composed from three calls rather than one, because the weekday sits
// after the year here and no arrangement of Intl options produces that
// order -- toLocaleString with weekday:'long' gives "Thursday, August
// 13, 2026 at 6:08:59 AM", weekday first and seconds included.
//
// Rendered in the viewer's own timezone, which is what the previous
// toLocaleString() calls did; these are "when did this actually happen
// to me" timestamps (last login, lead received), not the UTC calendar
// keys further down.
export const formatDateTime = (value) => {
  const d = asDate(value);
  if (!d) return '—';

  const date = d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  return `${date}, ${weekday} ${time}`;
};

// WhatsApp-style stamp for a dense list row: the time alone if it
// happened today, otherwise a short date. Deliberately not
// formatDateTime -- these sit at the right edge of every row in a long
// scrolling list, where the full format would dominate the row and wrap.
export const formatListStamp = (value) => {
  const d = asDate(value);
  if (!d) return '';

  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// The analytics payload's day buckets are plain "YYYY-MM-DD" calendar
// keys, not instants -- the server grouped events into UTC days, so
// these parse as UTC midnight and render in UTC. Without the pin, a bar
// the server labelled Aug 13 would read as Aug 12 to anyone west of
// Greenwich.
const asUtcDay = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Bar-chart axis ends and per-bar tooltips: "Aug 13". Thirty of these
// share one axis, so it stays as short as it can while still naming the
// day.
export const formatDayLabel = (dateStr) => {
  const d = asUtcDay(dateStr);
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// Date-only, spelled out, for prose: "August 13, 2026". Used where a
// sentence names the day a dataset starts, and a weekday or a time
// would be noise -- the boundary is a calendar date, not a moment.
export const formatLongDate = (dateStr) => {
  const d = asUtcDay(dateStr);
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

// Date-only, abbreviated: "Aug 13, 2026". For the small tag line under a
// summary card's number, where the long form wraps.
export const formatShortDate = (dateStr) => {
  const d = asUtcDay(dateStr);
  if (!d) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};
